import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { formatDayFromUtc } from '../../../utils/date';
import { roundValue } from '../../../utils/math';
import { queryHashGenerator } from '../../../utils/queryHash';
import { EntityType, IComputedAggregate, ISelectableTag } from '../../types';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import { IInclusiveDateOption } from '../settings/dates/dateRangeProvider';
import {
  eurReportSettingsProvider,
  IEurReportSettings,
} from '../settings/reports/eurReportSettingsProvider';
import { formatTimeframe } from '../utils/formatTimeframe';
import { isValidDateRange } from '../utils/isValidDateRange';
import { IReportResult, IReportState } from './types';

const cmjId = '7nNduHeM5zETPjHxvm7s';
const squatId = 'QEG7m7DhYsD6BrcQ8pic';

export interface IEur {
  timestamp: number;
  eur: number;
  squat: {
    value: number;
    stale: boolean;
    timestamp: number;
  };
  cmj: {
    value: number;
    stale: boolean;
    timestamp: number;
  };
  units: string;
}

export interface IEurResult extends IReportResult {
  eur: number | null;
  eurPercentageChange: number | null;
  eurTotalChange: number | null;
  eurStale: boolean;
  squat: number | null;
  squatStale: boolean;
  squatDate?: number;
  cmj: number | null;
  cmjStale: boolean;
  cmjDate?: number;
  displayDate: string;
  timestamp: number;
  end: number;
  units: string;
}

export interface IEurReportState extends IReportState {
  settings: IEurReportSettings;
  eurList: IEur[];
  resultList: IEurResult[];
  timeframe: string;
}

/**
 * @description pipeline that observes the aggregates requested based on the Eur report settings
 * 	and then determines and emits the state of the report
 * @param entityType
 * @param entityId
 * @returns
 */
export const eurReportProvider = (
  entityType: EntityType,
  entityId: string
): Observable<IEurReportState> => {
  return eurReportSettingsProvider().pipe(
    /// observe the aggregates as defined in the settings
    switchMap(
      (
        settings: IEurReportSettings
      ): Observable<
        [IEurReportSettings, ICrossTestAggregates, IHashManifest]
      > => {
        /// validate settings before fetching data
        if (validateSettings(settings).isValid) {
          const { timeframe, squatTags, cmjTags } = settings;
          /// date type is always `custom`
          const dateType: IInclusiveDateOption = {
            label: 'Custom',
            value: 'custom',
          };
          /// create query hash for squat tests
          const squatHash = queryHashGenerator(
            squatId,
            squatTags.map((t: ISelectableTag): string => t.value)
          );
          /// create query hash for cmj tests
          const cmjHash = queryHashGenerator(
            cmjId,
            cmjTags.map((t: ISelectableTag): string => t.value)
          );

          const hashManifest: IHashManifest = {
            cmjHash,
            squatHash,
          };

          return combineLatest<
            [IEurReportSettings, ICrossTestAggregates, IHashManifest]
          >([
            of<IEurReportSettings>(settings),
            crossTestAggregates(
              [squatHash, cmjHash],
              entityId,
              entityType,
              dateType,
              timeframe
            ),
            of(hashManifest),
          ]);
        } else {
          return combineLatest([of(settings), of(null), of(null)]);
        }
      }
    ),
    /// map the aggregates to a state
    map(
      (
        result: [IEurReportSettings, ICrossTestAggregates, IHashManifest]
      ): IEurReportState => {
        const [settings, aggregates, hashManifest] = result;
        const eurList = generateEurList(aggregates, hashManifest);
        const resultList = generateEurResultList(eurList);
        return {
          ...validateState(settings, resultList),
          settings,
          eurList,
          resultList,
          timeframe: formatTimeframe(
            settings.timeframe?.from?.value,
            settings.timeframe?.to?.value
          ),
        };
      }
    )
  );
};
/**
 * @description generates the result list by breaking the data into quartiles
 * @param eurList
 * @returns
 */
const generateEurResultList = (eurList: IEur[]): IEurResult[] => {
  if (eurList.length === 0) return [];
  const sortedEurList = sortBy(eurList, (eur: IEur): number => -eur.timestamp);
  const results: IEurResult[] = [];

  for (let i = 0; i < sortedEurList.length; i++) {
    const eur = sortedEurList[i];
    let change;
    if (i < sortedEurList.length - 1) {
      const nearestComparableEur = sortedEurList
        .slice(i + 1)
        .find((e: IEur): boolean => e.eur !== null);
      change = calculateChange(nearestComparableEur?.eur, eur?.eur);
    }
    results.push({
      eur: eur.eur,
      eurStale: eur.squat.stale || eur.cmj.stale,
      squat: eur.squat.value,
      squatStale: eur.squat.stale,
      squatDate: eur.squat.timestamp,
      cmj: eur.cmj.value,
      cmjStale: eur.cmj.stale,
      cmjDate: eur.cmj.timestamp,
      timestamp: eur.timestamp,
      displayDate: formatDayFromUtc(eur.timestamp),
      ...change,
    });
  }
  return results;
};

/**
 * @description calculates the change in eur values
 * @param previousEur
 * @param currentEur
 * @returns
 */
const calculateChange = (
  previousEur: number,
  currentEur: number
): { eurPercentageChange: number; eurTotalChange: number } => {
  if (currentEur !== null && previousEur !== null) {
    const eurTotalChange = roundValue(currentEur - previousEur, 2);
    return {
      eurTotalChange,
      eurPercentageChange: roundValue((eurTotalChange / previousEur) * 100, 1),
    };
  } else {
    return {
      eurTotalChange: null,
      eurPercentageChange: null,
    };
  }
};

/**
 * @description
 * @param aggregates
 * @param hashManifest
 * @returns
 */
const generateEurList = (
  aggregates: ICrossTestAggregates,
  hashManifest: IHashManifest
): IEur[] => {
  const cmjs = aggregates[hashManifest.cmjHash];
  const squats = aggregates[hashManifest.squatHash];

  const cmjSessionIds = cmjs.sessions.map(
    (ca: IComputedAggregate): string => ca.meta.id
  );

  const squatSessionIds = squats.sessions.map(
    (ca: IComputedAggregate): string => ca.meta.id
  );

  const uniqueSessionIds = uniq([...cmjSessionIds, ...squatSessionIds]);

  return uniqueSessionIds.map((sessionId: string): IEur => {
    const sameDayCmj = cmjs.sessions.find(
      (ca: IComputedAggregate): boolean => ca.meta.id === sessionId
    );
    const closestCmj = cmjs.sessions.find(
      (session: IComputedAggregate): boolean => session.meta.id < sessionId
    );
    const sameDaySquat = squats.sessions.find(
      (ca: IComputedAggregate): boolean => ca.meta.id === sessionId
    );

    const closestSquat = squats.sessions.find(
      (session: IComputedAggregate): boolean => session.meta.id < sessionId
    );

    const foundCmj = sameDayCmj ?? closestCmj;
    const foundSquat = sameDaySquat ?? closestSquat;

    const cmjPeak = Boolean(foundCmj)
      ? foundCmj.data.jumpHeight.topValues[0]
      : null;
    const squatPeak = Boolean(foundSquat)
      ? foundSquat.data.jumpHeight.topValues[0]
      : null;
    return {
      timestamp: +sessionId,
      cmj: {
        value: cmjPeak
          ? roundValue(cmjPeak, foundCmj.data.jumpHeight.roundingPrecision)
          : null,
        stale: !sameDayCmj,
        timestamp: +foundCmj?.meta?.id,
      },
      squat: {
        value: squatPeak
          ? roundValue(squatPeak, foundSquat.data.jumpHeight.roundingPrecision)
          : null,
        stale: !sameDaySquat,
        timestamp: +foundSquat?.meta?.id,
      },
      eur: cmjPeak && squatPeak ? roundValue(cmjPeak / squatPeak, 2) : null,
      units: foundCmj.data.jumpHeight.units,
    };
  });
};

interface IValidationResult {
  isValid: boolean;
  errorCode?: string;
}

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateState = (
  settings: IEurReportSettings,
  resultList: IEurResult[]
): IValidationResult => {
  const settingsValidation = validateSettings(settings);
  if (settingsValidation.isValid) {
    if (resultList.length === 0) {
      return {
        isValid: false,
        errorCode: 'insufficient-data',
      };
    } else {
      return {
        isValid: true,
      };
    }
  } else {
    return settingsValidation;
  }
};

const validateSettings = (settings: IEurReportSettings): IValidationResult => {
  if (
    !isValidDateRange({
      dateType: { label: 'Custom', value: 'custom' },
      dateRange: settings?.timeframe,
    })
  ) {
    return {
      isValid: false,
      errorCode: 'missing-date-range',
    };
  } else {
    return {
      isValid: true,
    };
  }
};

interface IHashManifest {
  cmjHash: string;
  squatHash: string;
}
